import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import {
    CreateChancel,
    Input,
    CheckboxCustom,
    SelectAutocompleteMultiple,
    DateCalendarForm,
    NumericFormatCustom,
    TextareaCustom,
    AddCircleBlue,
    UploadFiles,
    CloseButton,
} from 'components';
import {
    claimPaymentActions,
    fundingSourceActions,
    httpRequestsOnErrorsActions,
    httpRequestsOnSuccessActions,
    systemActions,
    deleteFile as deleteRemoteFile,
} from 'store';
import { FindLoad, FindSuccessItem, useModal, useSecondaryModal, Images } from 'utils';
import { paymentMethods } from '../constants';
import { UploadedSignature } from '../../../../fragments/schedule/core/common/uploadedSignature';
import { SelectClaimPayment } from './selectClaimPayment';

export const AddClaimPayment = ({ info, afterSuccess, edit }) => {
    const ACTION_TYPE = info ? 'EDIT_REMITTANCE' : 'CREATE_REMITTANCE';
    const fundingSourceList = useSelector((state) => state.fundingSource.fundingSourceList);
    const bankList = useSelector((state) => state.system.bankList);
    const dispatch = useDispatch();
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const { open, close } = useModal();
    const {
        handleSubmit,
        control,
        clearErrors,
        reset,
        watch,
        formState: { errors },
    } = useForm();
    const { openSecondary } = useSecondaryModal();
    const [uploadedFile, setUploadedFile] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (success && !info) {
            openSecondary(<SelectClaimPayment />);
            if (success?.data?.id) {
                navigate(`/claim-payment/${success?.data?.id}`);
            }
            close();

            afterSuccess();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
        if (success && edit) {
            close();
            afterSuccess?.();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    useEffect(() => {
        dispatch(fundingSourceActions.getFundingSource({ status: 'ACTIVE' }));
        dispatch(systemActions.getBanks());
    }, []);

    useEffect(() => {
        if (info) {
            reset(info);
            setUploadedFile(info?.eob?.file ? info?.eob : null);
        }
    }, [info]);

    useEffect(() => {
        return () => {
            clearErrors();
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        };
    }, []);
    const handleCreate = (data) => {
        const sendData = {
            ...data,
        };

        uploadedFile ? (sendData.eob = uploadedFile) : delete sendData.eob;
        if (info) {
            dispatch(claimPaymentActions.editRemittance(info.id, sendData));
        } else {
            dispatch(claimPaymentActions.createRemittance(sendData));
        }
    };

    const deleteFile = () => {
        const k = uploadedFile?.file?.key;
        if (!k) return;
        if (info?.id) {
            if (uploadedFile?.file !== info?.eob?.file) {
                deleteRemoteFile(k).catch(() => {});
            }
        } else {
            if (uploadedFile) {
                deleteRemoteFile(k).catch(() => {});
            }
        }
    };

    return (
        <div style={{ width: 780 }}>
            <div className="space-between-flex-start">
                <p className="modal-header-title">{info ? 'Edit Claim Payment' : 'Add Claim Payment'}</p>
                <div className="modal-header-style" style={{ marginTop: '-25px' }}>
                    <CloseButton
                        handleCLic={() => {
                            close();
                            deleteFile();
                        }}
                    />
                </div>
            </div>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper">
                    <div className="flex-align-start" style={{ gap: 24 }}>
                        <div className="full-width">
                            <DateCalendarForm
                                label={'Payment Date*'}
                                name={'paymentDate'}
                                control={control}
                                type={'modalInput'}
                                rules={{
                                    required: 'Payment date is required.',
                                }}
                            />
                            <NumericFormatCustom
                                name={'totalAmount'}
                                label={'Payment Amount*'}
                                prefix={'$'}
                                thousandSeparator={true}
                                fixedDecimalScale={true}
                                decimalScale={2}
                                // decimalScale={4}
                                control={control}
                                rules={{
                                    required: 'Payment amount is required.',
                                }}
                            />
                            <Input
                                name="paymentReference"
                                label="Payment Reference #"
                                placeholder={'e.g. Check #12345'}
                                variant="outlined"
                                control={control}
                                // rules={{
                                //     required: 'Payment reference is required.',
                                // }}
                            />
                            <div style={{ opacity: watch('totalAmount') ? 1 : 0.5 }}>
                                <Controller
                                    name="adjustmentAmount"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            const openAmt = parseFloat(watch('openAmount') || 0);
                                            const total = parseFloat(watch('totalAmount') || 0);
                                            const appliedAmount = info?.appliedAmount || 0;
                                            const adj = parseFloat(value || 0);

                                            if (info?.id) {
                                                if (info?.adjustmentAmount !== value) {
                                                    if (appliedAmount + adj > total) {
                                                        return `Open Amount cannot be negative ($${openAmt - adj})`;
                                                    }
                                                }
                                            } else {
                                                if (adj > total) {
                                                    return `Adjustment cannot exceed Payment Amount ($${total})`;
                                                }
                                            }
                                            return true;
                                        },
                                    }}
                                    render={({ field, fieldState }) => (
                                        <NumericFormatCustom
                                            disabled={!watch('totalAmount')}
                                            name={'adjustmentAmount'}
                                            label={'Adjustment AMT'}
                                            prefix={'$'}
                                            thousandSeparator={true}
                                            fixedDecimalScale={true}
                                            decimalScale={2}
                                            // decimalScale={4}
                                            control={control}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="full-width">
                            <SelectAutocompleteMultiple
                                name="payerId"
                                label={'Payer*'}
                                control={control}
                                options={fundingSourceList?.funders}
                                rules={{ required: 'Payer is required.' }}
                                renderValue={(option) => option?.name}
                                // defaultSelected={!watch('payerName') && {
                                //     name: watch('payerName'),
                                //     id: watch('payerId'),
                                // }}
                            />
                            <SelectAutocompleteMultiple
                                name="paymentMethod"
                                label={'Payment Method*'}
                                control={control}
                                options={paymentMethods}
                                rules={{ required: 'Payment method is required.' }}
                                renderValue={(option) => option?.name}
                                defaultSelected={
                                    !watch('paymentMethod') && {
                                        name: watch('paymentMethod'),
                                        id: watch('paymentMethod'),
                                    }
                                }
                            />
                            <SelectAutocompleteMultiple
                                name="bankId"
                                label={'Bank Name'}
                                control={control}
                                options={bankList}
                                // rules={{ required: 'Bank is required.' }}
                                renderValue={(option) => option?.name}
                                defaultSelected={
                                    !watch('bankId') && {
                                        name: watch('bankName'),
                                        id: watch('bankId'),
                                    }
                                }
                            />
                        </div>
                    </div>

                    <div className="flex-align-center" style={{ gap: 24 }}>
                        <div className="full-width">
                            <TextareaCustom
                                styles={{ width: '100%' }}
                                control={control}
                                name="adjustmentReason"
                                label="Adjustment Reason"
                                variant={'outlined'}
                            />
                        </div>
                        <div className="full-width">
                            <TextareaCustom styles={{ width: '100%' }} control={control} name="memo" label="Memo" variant={'outlined'} />
                        </div>
                    </div>

                    <div className="space-between">
                        <AddCircleBlue
                            icon={Images.uploadDockCloud}
                            text={'Upload EOB/ERA'}
                            handleClick={() =>
                                openSecondary(
                                    <UploadFiles
                                        noDescription={true}
                                        secondaryModal={true}
                                        onResource={'getFile'}
                                        singleFile={true}
                                        getUploadedFile={(file) => {
                                            deleteFile();
                                            setUploadedFile(file?.length ? file[0] : null);
                                        }}
                                    />
                                )
                            }
                        />
                        <CheckboxCustom name="verified" label="Mark this payment as Verified" control={control} />
                    </div>

                    {uploadedFile?.file && (
                        <div className="flex-align-center" style={{ gap: 24, marginTop: 12 }}>
                            <UploadedSignature
                                textWidth={'230px'}
                                alwaysDelete={true}
                                handleRemoveUploaded={() => {
                                    deleteFile();
                                    setUploadedFile(null);
                                }}
                                currentFile={uploadedFile}
                                alreadyUploaded={true}
                            />
                            <div />
                        </div>
                    )}
                </div>
                <CreateChancel
                    type="submit"
                    loader={!!loader.length}
                    create={info ? 'Save' : 'Next'}
                    chancel={'Cancel'}
                    onClose={() => {
                        deleteFile();
                        close();
                    }}
                    buttonWidth="100%"
                    cancelRight={'24px'}
                />
            </form>
        </div>
    );
};
